import { useMemo } from 'react'

import moment from 'moment'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction from 'modules/core/lib/asyncAction'
import { stringToDate } from 'modules/core/lib/dates'

import type Employee from 'modules/employees/store/models/Employee'
import Form from 'modules/timeoff/lib/LeaveUpdateForm'
import leaves from 'modules/timeoff/store/collections/leaves'
import medicalLeaveUpdates from 'modules/timeoff/store/collections/medicalLeaveUpdates'
import type Leave from 'modules/timeoff/store/models/Leave'
import MedicalLeaveUpdate from 'modules/timeoff/store/models/MedicalLeaveUpdate'

interface Props {
  employee: Employee | null | undefined
  leave: Leave
  onClose: () => void
}
interface ReturnType {
  onSubmit: () => void
  isProcessing: boolean
  minEffectiveOn: moment.Moment
  form: Form
}

const { dischargeReasons, types: medicalTypes } = MedicalLeaveUpdate

const useCreateMedicalLeaveUpdate = ({ employee, leave, onClose }: Props): ReturnType => {
  const minEffectiveOn = stringToDate(leave.attributes.start_on, 'backend')

  const form = useMemo(
    () =>
      new Form({
        leave_id: leave.attributes.id,
        effective_on: leave.attributes.start_on,
        medical_discharge_reason: dischargeReasons.recovery_professional_capacity,
        leave_update_type: medicalTypes.medical_discharge,
      }),
    [leave]
  )

  const { running: isProcessing, performAction: onSubmit } = useAsyncAction(async () => {
    const { success } = await asyncAction(async () => {
      await form.create(medicalLeaveUpdates)

      const year = moment().year()
      leaves.fetch({
        data: {
          year,
        },
      })
    })

    if (success) {
      onClose()

      if (employee) {
        await medicalLeaveUpdates.fetch({
          data: {
            employee_id: employee?.attributes.id,
          },
        })
      }
    }
  })

  return {
    onSubmit,
    isProcessing,
    minEffectiveOn,
    form,
  }
}

export default useCreateMedicalLeaveUpdate
